/* SpawnAnything — pure weapon lookups shared by sound and VFX (no three/DOM, tested in Node):
   which sound a weapon makes, how its projectile looks, and which army fired a projectile
   (the sim's projectile stream only carries kind + arc flag + velocity, so we match against both armies' weapons). */

/** melee impact sound per weapon key (fallback by weapon traits) */
const MELEE = { faeuste: 'punch', pranke: 'punch', stosszaehne: 'punch', keule: 'bonk', flammenwerfer: 'flame' };
/** ranged fire sound per weapon key */
const RANGED = {
  gewehr: 'rifle', karabiner: 'rifle', scharfschuetzengewehr: 'rifle', pistole: 'pistol', muskete: 'musket',
  mg: 'mg', minigun: 'mg', sturmgewehr: 'mg',
  laserpistole: 'laser', lasergewehr: 'laser', railgun: 'railgun', plasmagewehr: 'plasma', zauber: 'plasma',
  bogen: 'bow', armbrust: 'bow', wurfspeer: 'throw', steinwurf: 'throw',
  raketen: 'rocket', raketenwerfer_modern: 'rocket', kanone: 'cannon', huhnkanone: 'honk', giftgaswerfer: 'gasshot',
};

/** sound category for a weapon: { fire, melee } (one of them null) */
export function weaponSound(key, wp, fx) {
  if (!wp) return { fire: 'rifle', melee: null };
  if (wp.melee) return { fire: null, melee: MELEE[key] || (fx && fx.swing === 'fire' ? 'flame' : wp.cleave && /plasma/.test(key) ? 'zap' : 'clash') };
  if (RANGED[key]) return { fire: RANGED[key], melee: null };
  if (fx && fx.tracer === 'laser') return { fire: fx.width > 1 ? 'railgun' : 'laser', melee: null };
  if (fx && fx.projectile === 'plasma') return { fire: 'plasma', melee: null };
  if (fx && fx.projectile === 'arrow') return { fire: 'bow', melee: null };
  if (fx && fx.projectile === 'stone') return { fire: 'throw', melee: null };
  if (fx && fx.projectile === 'gas') return { fire: 'gasshot', melee: null };
  if (wp.hitscan) return { fire: wp.cd < 0.4 ? 'mg' : 'rifle', melee: null };
  return { fire: wp.proj === 1 ? 'cannon' : wp.proj === 2 ? 'throw' : 'rocket', melee: null };
}
/** fire sound for a building gun / unknown shooter, by projectile kind (hitscan -> null) */
export const kindSound = kind => kind === 1 ? 'cannon' : kind === 2 ? 'throw' : kind === 4 ? 'gasshot' : 'rocket';

const hex = (h, k = 1) => [((h >> 16) & 255) / 255 * k, ((h >> 8) & 255) / 255 * k, (h & 255) / 255 * k];
const css = s => typeof s === 'string' && s[0] === '#' ? parseInt(s.slice(1), 16) : null;

/**
 * Projectile look for a weapon: { L, w, r, g, b, trail } where trail is null | 'smoke' | 'plasma' | 'gas' | 'spark'.
 * plasmaHex = the firing team's plasma colour for 'team-plasma'.
 */
export function projStyle(key, wp, fx, plasmaHex = 0x6ff0ff) {
  const p = fx && fx.projectile;
  const c = fx && (fx.color === 'team-plasma' ? plasmaHex : css(fx.color));
  if (p === 'arrow') { const [r, g, b] = hex(c ?? 0xe8e2d2); return key === 'wurfspeer' ? { L: 1.6, w: 0.09, r, g, b, trail: null } : { L: 0.95, w: 0.07, r, g, b, trail: null }; }
  if (p === 'stone') { const [r, g, b] = hex(c ?? 0x8a8f96); return { L: 0.34, w: 0.34, r, g, b, trail: null }; }
  if (p === 'plasma') { const [r, g, b] = hex(c ?? plasmaHex, 2.2); return { L: 1.0, w: 0.36, r, g, b, trail: 'plasma' }; }
  if (p === 'gas') { const [r, g, b] = hex(c ?? 0x7cff4f, 1.1); return { L: 0.5, w: 0.5, r, g, b, trail: 'gas' }; }
  return kindStyle(wp ? wp.proj : 0);
}
const KIND_STYLES = [
  { L: 0.9, w: 0.2, r: 0.9, g: 0.9, b: 0.85, trail: 'smoke' }, // 0 rocket
  { L: 1.4, w: 0.22, r: 2, g: 1.6, b: 0.7, trail: null }, // 1 shell
  { L: 0.5, w: 0.42, r: 1.3, g: 1.25, b: 1.1, trail: null }, // 2 lob (rubber chicken)
];
/** default look by projectile kind (the renderer's original styles) */
export const kindStyle = kind => KIND_STYLES[kind] || KIND_STYLES[0];

/**
 * Which candidate fired a projectile? cands: [{ proj, arc, pspeed }] (one per army; null for melee/hitscan).
 * Matches kind + arc flag, then the closest launch speed (horizontal speed for arcs: gravity leaves it unchanged).
 * Returns the candidate index or -1.
 */
export function matchShooter(cands, kind, arc, vx, vy, vz) {
  let best = -1, bd = Infinity;
  const sp = arc ? Math.hypot(vx, vz) : Math.hypot(vx, vy, vz);
  for (let i = 0; i < cands.length; i++) {
    const c = cands[i]; if (!c || c.proj !== kind || !!c.arc !== arc) continue;
    const d = Math.abs(sp - c.pspeed) / (c.pspeed || 1);
    if (d < bd) { bd = d; best = i; }
  }
  return bd < 0.35 ? best : -1;
}
